import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGetJson } from "../../utils/apiClient";
import { CsvCol, downloadCsv } from "../../utils/csvExport";

type MetricsRow = {
  tenantId: string;
  companyId: string;
  shopUrl: string;
  companyName?: string;
  shopName?: string;
  contractType?: string;
  trialStatus?: string;
  plan?: string;
  lastMonthSales?: number;
  lastMonthAdCost?: number;
};

function normalize(v: any): string {
  return String(v ?? "").trim().toLowerCase();
}

function fmtYen(v: any): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `¥${Math.round(n).toLocaleString("ja-JP")}`;
}

export default function SuperCompanyMonthlyMetricsPage() {
  const [rows, setRows] = useState<MetricsRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetJson("/api/super/companies?include=lastMonthMetrics");
      setRows(Array.isArray(data) ? (data as MetricsRow[]) : []);
    } catch (e: any) {
      setError(e?.message ?? "前月売上/広告費の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = normalize(search);
    const list = rows.filter((r) => {
      if (!q) return true;
      return [r.shopUrl, r.companyName, r.shopName].some((x) => normalize(x).includes(q));
    });

    return list.sort((a, b) => {
      const sa = Number(a.lastMonthSales ?? 0);
      const sb = Number(b.lastMonthSales ?? 0);
      if (sb !== sa) return sb - sa;
      return String(a.shopUrl || "").localeCompare(String(b.shopUrl || ""), "ja");
    });
  }, [rows, search]);

  const download = useCallback(() => {
    const csvRows = filtered.map((r) => ({
      tenantId: r.tenantId,
      shopUrl: r.shopUrl,
      companyName: r.companyName || "",
      shopName: r.shopName || "",
      contractType: r.contractType || "",
      trialStatus: r.trialStatus || "",
      plan: r.plan || "",
      lastMonthSales: r.lastMonthSales == null ? "" : String(Math.round(Number(r.lastMonthSales) || 0)),
      lastMonthAdCost: r.lastMonthAdCost == null ? "" : String(Math.round(Number(r.lastMonthAdCost) || 0)),
      companyId: r.companyId,
    }));

    const cols: CsvCol<(typeof csvRows)[0]>[] = [
      { key: "tenantId", header: "テナント" },
      { key: "shopUrl", header: "ショップURL" },
      { key: "companyName", header: "会社名" },
      { key: "shopName", header: "ショップ名" },
      { key: "contractType", header: "契約内容" },
      { key: "trialStatus", header: "契約ステータス" },
      { key: "plan", header: "プラン" },
      { key: "lastMonthSales", header: "前月売上" },
      { key: "lastMonthAdCost", header: "前月広告費" },
      { key: "companyId", header: "会社ID" },
    ];

    downloadCsv(`company_monthly_metrics_${dayjs().format("YYYYMMDDHHmmss")}`, csvRows, cols);
  }, [filtered]);

  return (
    <div className="w-full max-w-none min-w-0 h-full flex flex-col gap-2 px-3 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-lg font-bold">前月売上・前月広告費（SuperAdmin）</h1>

          <input
            type="text"
            placeholder="ショップURL / 企業名 / ショップ名で検索"
            className="border px-2 py-1 rounded w-72 text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="text-xs text-slate-600">
            {loading ? "loading..." : `${filtered.length} 件（全 ${rows.length} 件）`}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href="/super/companies"
            className="border px-2 py-1 rounded text-xs bg-white text-slate-900 hover:bg-slate-50"
          >
            会社一覧へ戻る
          </a>
          <button
            className="border px-2 py-1 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
            disabled={loading || filtered.length === 0}
            onClick={download}
          >
            CSV出力
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <div className="flex-1 min-h-0 overflow-auto w-full">
        <table className="w-max min-w-full table-fixed border-separate border-spacing-0">
          <thead className="font-semibold">
            <tr>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">テナント</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">ショップURL</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">会社名</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">ショップ名</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">契約内容</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">契約ステータス</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">プラン</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">前月売上</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">前月広告費</th>
              <th className="border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs">会社ID</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="border px-2 py-1 text-xs text-gray-500">
                  読み込み中...
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="border px-2 py-1 text-xs text-gray-500">
                  該当する企業がありません
                </td>
              </tr>
            )}

            {filtered.map((r) => (
              <tr key={`${r.tenantId}__${r.companyId}__${r.shopUrl}`} className="hover:bg-gray-50">
                <td className="border px-2 py-1 text-xs">{r.tenantId}</td>
                <td className="border px-2 py-1 text-xs">{r.shopUrl}</td>
                <td className="border px-2 py-1 text-xs">{r.companyName || ""}</td>
                <td className="border px-2 py-1 text-xs">{r.shopName || ""}</td>
                <td className="border px-2 py-1 text-xs">{r.contractType || ""}</td>
                <td className="border px-2 py-1 text-xs">{r.trialStatus || ""}</td>
                <td className="border px-2 py-1 text-xs">{r.plan || ""}</td>
                <td className="border px-2 py-1 text-xs text-right tabular-nums">{fmtYen(r.lastMonthSales)}</td>
                <td className="border px-2 py-1 text-xs text-right tabular-nums">{fmtYen(r.lastMonthAdCost)}</td>
                <td className="border px-2 py-1 text-xs">{r.companyId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
